import * as Phaser from 'phaser';
import { GameSettingsContent } from '../data';

export type CombatFeedbackEffectKey = keyof NonNullable<GameSettingsContent['combatFeedback']>;

export interface ChainFeedbackPayload { readonly x: number; readonly y: number; readonly toX: number; readonly toY: number; }
export interface LootCollectedFeedbackPayload { readonly x: number; readonly y: number; readonly kind: 'gem' | 'chest'; }

export interface EnemyHitFeedbackPayload {
  readonly enemy: Phaser.Physics.Arcade.Sprite;
  readonly id: string;
  readonly damage: number;
  readonly currentHealth: number;
  readonly maxHealth: number;
  readonly kind: 'primary' | 'chain' | 'area';
  readonly critical?: boolean;
  readonly effects?: {
    readonly burn?: { damagePerSecond: number; expiresAt: number };
    readonly slow?: { multiplier: number; expiresAt: number };
  };
  readonly x: number;
  readonly y: number;
}

export interface EnemyKilledFeedbackPayload {
  readonly enemy: Phaser.Physics.Arcade.Sprite;
  readonly id: string;
  readonly x: number;
  readonly y: number;
}

export interface PlayerHitFeedbackPayload {
  readonly player?: Phaser.Physics.Arcade.Sprite;
  readonly damage: number;
  readonly currentHp: number;
  readonly suppressedDeath?: boolean;
}

export interface PlayerDeadFeedbackPayload {
  readonly player?: Phaser.Physics.Arcade.Sprite;
}

export interface BossSpawnedFeedbackPayload {
  readonly bossEncounter?: {
    readonly encounterId: string;
  };
}

export interface BossKilledFeedbackPayload {
  readonly x?: number;
  readonly y?: number;
  readonly bossEncounter?: {
    readonly encounterId: string;
  };
}

export interface LevelUpFeedbackPayload {
  readonly level?: number;
}

export interface ChestOpenedFeedbackPayload {
  readonly mode?: 'evolution' | 'upgrades';
}

export interface ChestClosedFeedbackPayload {
  readonly evolution?: unknown;
  readonly upgrades?: unknown;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export function isEnemyHitFeedbackPayload(value: unknown): value is EnemyHitFeedbackPayload {
  const payload = value as Partial<EnemyHitFeedbackPayload>;
  return Boolean(
    payload
    && payload.enemy
    && typeof payload.id === 'string'
    && isFiniteNumber(payload.damage)
    && isFiniteNumber(payload.currentHealth)
    && isFiniteNumber(payload.maxHealth)
    && (payload.kind === 'primary' || payload.kind === 'chain' || payload.kind === 'area')
    && isFiniteNumber(payload.x)
    && isFiniteNumber(payload.y)
  );
}

export function isEnemyKilledFeedbackPayload(value: unknown): value is EnemyKilledFeedbackPayload {
  const payload = value as Partial<EnemyKilledFeedbackPayload>;
  return Boolean(
    payload
    && payload.enemy
    && typeof payload.id === 'string'
    && isFiniteNumber(payload.x)
    && isFiniteNumber(payload.y)
  );
}

export function isPlayerHitFeedbackPayload(value: unknown): value is PlayerHitFeedbackPayload {
  const payload = value as Partial<PlayerHitFeedbackPayload>;
  return Boolean(payload && isFiniteNumber(payload.currentHp) && isFiniteNumber(payload.damage));
}

export function isPlayerDeadFeedbackPayload(value: unknown): value is PlayerDeadFeedbackPayload {
  return value === undefined || (typeof value === 'object' && value !== null);
}

export function isBossKilledFeedbackPayload(value: unknown): value is BossKilledFeedbackPayload {
  if (value === undefined || value === null) {
    return true;
  }

  const payload = value as Partial<BossKilledFeedbackPayload>;
  return payload.bossEncounter === undefined
    || typeof payload.bossEncounter.encounterId === 'string';
}

export function isChestClosedFeedbackPayload(value: unknown): value is ChestClosedFeedbackPayload {
  if (value === undefined || value === null) {
    return true;
  }

  const payload = value as Partial<ChestClosedFeedbackPayload>;
  return Boolean(
    typeof value === 'object'
    && (
      payload.evolution !== undefined
      || payload.upgrades !== undefined
      || Object.keys(value as Record<string, unknown>).length === 0
    )
  );
}
